import { Activity, Ban, MoreHorizontal, Network, Pencil, RefreshCcw } from "lucide-react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { usePermissionLabel } from "../hooks/usePermissionLabel";
import type { InternalApiKey } from "../types/internalApiKeys.types";
import { STATUS_TONE } from "../utils/internalApiKeyForm";

export interface InternalApiKeyRowProps {
  apiKey: InternalApiKey;
  isBusy: boolean;
  onToggle: (apiKey: InternalApiKey, next: boolean) => void;
  onEdit: (apiKey: InternalApiKey) => void;
  onEditIps: (apiKey: InternalApiKey) => void;
  onRotate: (apiKey: InternalApiKey) => void;
  onUsage: (apiKey: InternalApiKey) => void;
  onRevoke: (apiKey: InternalApiKey) => void;
}

/** One key: name and state, what it can reach, what it can do, then its last use. */
export function InternalApiKeyRow({
  apiKey,
  isBusy,
  onToggle,
  onEdit,
  onEditIps,
  onRotate,
  onUsage,
  onRevoke,
}: InternalApiKeyRowProps) {
  const { t, activeLanguage } = useTranslations("internalApiKeys");
  const permissionLabel = usePermissionLabel();
  const date = (value: string) => formatDateTime(value, { locale: activeLanguage, includeTime: false });
  const dateTime = (value: string) => formatDateTime(value, { locale: activeLanguage, includeTime: true });
  const revoked = apiKey.status === "REVOKED";
  const canTurnOn = apiKey.status !== "NO_IP" && apiKey.status !== "EXPIRED";
  const switchHint =
    apiKey.status === "NO_IP"
      ? t("switch_needs_ip")
      : apiKey.status === "EXPIRED"
        ? t("switch_needs_expiry")
        : null;

  const organizations = apiKey.all_organizations
    ? t("row_all_organizations")
    : apiKey.organization_ids.length === 1
      ? t("row_organizations_one")
      : t("row_organizations_other", { count: apiKey.organization_ids.length });
  const permissions = apiKey.all_permissions
    ? t("row_permissions_all")
    : t("row_permissions", { list: apiKey.permissions.map(permissionLabel).join(", ") });
  const ipList = apiKey.allowed_ips.map((entry) => entry.ip).join(", ");
  const ips =
    apiKey.allowed_ips.length === 0
      ? t(apiKey.allow_low_risk_without_ip ? "row_no_ip_low_risk" : "row_no_ip")
      : t(apiKey.allow_low_risk_without_ip ? "row_ips_low_risk" : "row_ips", { ips: ipList });
  const lifetime = revoked
    ? t("row_revoked", { date: date(apiKey.revoked_at ?? apiKey.updated_at) })
    : apiKey.status === "EXPIRED"
      ? t("row_expired", { date: date(apiKey.expires_at) })
      : t("row_expires", { date: date(apiKey.expires_at) });
  const lastUse = apiKey.last_used_at
    ? t("row_last_used", { date: dateTime(apiKey.last_used_at), ip: apiKey.last_used_ip ?? "—" })
    : t("row_never_used");
  const grace =
    !revoked && apiKey.previous_secret_expires_at && new Date(apiKey.previous_secret_expires_at) > new Date()
      ? t("row_rotation_grace", { date: dateTime(apiKey.previous_secret_expires_at) })
      : null;

  const items = revoked
    ? [{ label: t("action_usage"), icon: Activity, onClick: () => onUsage(apiKey) }]
    : [
        { label: t("action_edit"), icon: Pencil, onClick: () => onEdit(apiKey) },
        { label: t("action_edit_ips"), icon: Network, onClick: () => onEditIps(apiKey) },
        { label: t("action_rotate"), icon: RefreshCcw, onClick: () => onRotate(apiKey) },
        { label: t("action_usage"), icon: Activity, onClick: () => onUsage(apiKey) },
        {
          label: t("action_revoke"),
          icon: Ban,
          variant: "destructive" as const,
          onClick: () => onRevoke(apiKey),
        },
      ];

  return (
    <li className="flex items-start gap-3 px-4 py-3 sm:px-5">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-foreground">{apiKey.name}</span>
          <ColocaStatusPill variant={STATUS_TONE[apiKey.status]}>
            {t(`status_${apiKey.status}`)}
          </ColocaStatusPill>
          <code className="font-mono text-xs text-muted-foreground">{apiKey.key_id}</code>
        </div>
        <p className="text-sm text-muted-foreground">{[organizations, ips, lifetime].join(" · ")}</p>
        <p className="text-sm text-muted-foreground">{permissions}</p>
        <p className="text-xs text-muted-foreground">
          {[lastUse, grace, switchHint].filter(Boolean).join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!revoked && (
          <ColocaSwitch
            checked={apiKey.is_active}
            disabled={isBusy || (!apiKey.is_active && !canTurnOn)}
            onCheckedChange={(next) => onToggle(apiKey, next)}
            aria-label={t("switch_label", { name: apiKey.name })}
          />
        )}
        <ColocaDropdownMenu
          align="end"
          items={items}
          trigger={
            <ColocaIconButton
              icon={MoreHorizontal}
              variant="ghost"
              size="sm"
              aria-label={t("menu_label", { name: apiKey.name })}
            />
          }
        />
      </div>
    </li>
  );
}
